import { parseNaturalInput } from '@/_pure/naturalInput';
import type {
    PrimitiveOperationInput,
    PrimitiveOperationInputDraft,
    PrimitivePresentationOperation,
} from '@/adapters/primitive/_contract/operation';

export const SECTION_NATURAL_OPERAND_ONE = 'primitive-natural-operand-one';
export const SECTION_NATURAL_OPERAND_TWO = 'primitive-natural-operand-two';
export const SECTION_NATURAL_INPUT = 'primitive-natural-input';
export const SECTION_NATURAL_LESS_THAN_SOURCE = 'primitive-natural-less-than-source';
export const SECTION_NATURAL_LESS_THAN_TARGET = 'primitive-natural-less-than-target';
export const SECTION_BOOLEAN_INPUT = 'primitive-boolean-input';
export const SECTION_BOOLEAN_OPERAND_ONE = 'primitive-boolean-operand-one';
export const SECTION_BOOLEAN_OPERAND_TWO = 'primitive-boolean-operand-two';

const MAX_DIGITS_BY_OPERATION: Record<PrimitivePresentationOperation, number | undefined> = {
    addition: 3,
    subtraction: 3,
    multiplication: 2,
    division: 2,
    'natural-identity': 3,
    'boolean-identity': undefined,
    'logical-not': undefined,
    'logical-and': undefined,
    'logical-nand': undefined,
    'logical-xor': undefined,
    'logical-xnor': undefined,
    'logical-or': undefined,
    'logical-nor': undefined,
    'natural-less-than': 3,
};

export function toPrimitiveOperationInputDraft(input: PrimitiveOperationInput): PrimitiveOperationInputDraft {
    switch (input.operation) {
        case 'addition':
        case 'subtraction':
        case 'multiplication':
        case 'division':
            return { operation: input.operation, operandOneText: String(input.operandOne), operandTwoText: String(input.operandTwo) };
        case 'natural-identity':
            return { operation: input.operation, inputText: String(input.input) };
        case 'boolean-identity':
        case 'logical-not':
            return { operation: input.operation, inputText: input.input ? 'true' : 'false' };
        case 'natural-less-than':
            return { operation: input.operation, sourceText: String(input.source), targetText: String(input.target) };
        default:
            return { operation: input.operation, operandOneText: input.operandOne ? 'true' : 'false', operandTwoText: input.operandTwo ? 'true' : 'false' };
    }
}

export function resolvePrimitiveOperationInput(
    draft: PrimitiveOperationInputDraft,
): PrimitiveOperationInput | null {
    const maxDigits = MAX_DIGITS_BY_OPERATION[draft.operation];
    switch (draft.operation) {
        case 'addition':
        case 'subtraction':
        case 'multiplication':
        case 'division': {
            const operandOne = parseNaturalInput(draft.operandOneText, { maxDigits });
            const operandTwo = parseNaturalInput(draft.operandTwoText, { maxDigits });
            if (operandOne === null || operandTwo === null) return null;
            return { operation: draft.operation, operandOne, operandTwo };
        }
        case 'natural-identity': {
            const input = parseNaturalInput(draft.inputText, { maxDigits });
            return input === null ? null : { operation: draft.operation, input };
        }
        case 'boolean-identity':
        case 'logical-not':
            return draft.inputText === '' ? null : {
                operation: draft.operation,
                input: draft.inputText === 'true',
            };
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return draft.operandOneText === '' || draft.operandTwoText === '' ? null : {
                operation: draft.operation,
                operandOne: draft.operandOneText === 'true',
                operandTwo: draft.operandTwoText === 'true',
            };
        case 'natural-less-than': {
            const source = parseNaturalInput(draft.sourceText, { maxDigits });
            const target = parseNaturalInput(draft.targetText, { maxDigits });
            return source === null || target === null ? null : { operation: draft.operation, source, target };
        }
    }
}

function naturalInputError(args: {
    value: string;
    maxDigits: number;
}): string | undefined {
    if (args.value === '') return undefined;
    const parsed = parseNaturalInput(args.value, { maxDigits: args.maxDigits });
    if (parsed === null) return `Enter a non-negative integer with at most ${args.maxDigits} digits.`;
    return undefined;
}

export function getPrimitiveOperationInputError(
    draft: PrimitiveOperationInputDraft,
    sectionId: string,
): string | undefined {
    const maxDigits = MAX_DIGITS_BY_OPERATION[draft.operation];
    switch (draft.operation) {
        case 'addition':
        case 'subtraction':
        case 'multiplication':
        case 'division':
            if (sectionId === SECTION_NATURAL_OPERAND_ONE) {
                return naturalInputError({ value: draft.operandOneText, maxDigits: maxDigits! });
            }
            if (sectionId === SECTION_NATURAL_OPERAND_TWO) {
                return naturalInputError({
                    value: draft.operandTwoText,
                    maxDigits: maxDigits!,
                });
            }
            return undefined;
        case 'natural-identity':
            return sectionId === SECTION_NATURAL_INPUT
                ? naturalInputError({ value: draft.inputText, maxDigits: maxDigits! })
                : undefined;
        case 'boolean-identity':
        case 'logical-not':
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return undefined;
        case 'natural-less-than':
            if (sectionId === SECTION_NATURAL_LESS_THAN_SOURCE) {
                return naturalInputError({ value: draft.sourceText, maxDigits: maxDigits! });
            }
            return sectionId === SECTION_NATURAL_LESS_THAN_TARGET
                ? naturalInputError({ value: draft.targetText, maxDigits: maxDigits! })
                : undefined;
    }
}

export function updatePrimitiveOperationInputDraft(args: {
    draft: PrimitiveOperationInputDraft;
    sectionId: string;
    value: string;
}): PrimitiveOperationInputDraft | null {
    const { draft, sectionId, value } = args;
    switch (draft.operation) {
        case 'addition':
        case 'subtraction':
        case 'multiplication':
        case 'division':
            if (sectionId === SECTION_NATURAL_OPERAND_ONE) return { ...draft, operandOneText: value };
            if (sectionId === SECTION_NATURAL_OPERAND_TWO) return { ...draft, operandTwoText: value };
            return null;
        case 'natural-identity':
            return sectionId === SECTION_NATURAL_INPUT ? { ...draft, inputText: value } : null;
        case 'boolean-identity':
        case 'logical-not':
            return sectionId === SECTION_BOOLEAN_INPUT && (value === '' || value === 'true' || value === 'false')
                ? { ...draft, inputText: value }
                : null;
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            if (value !== '' && value !== 'true' && value !== 'false') return null;
            if (sectionId === SECTION_BOOLEAN_OPERAND_ONE) return { ...draft, operandOneText: value };
            if (sectionId === SECTION_BOOLEAN_OPERAND_TWO) return { ...draft, operandTwoText: value };
            return null;
        case 'natural-less-than':
            if (sectionId === SECTION_NATURAL_LESS_THAN_SOURCE) return { ...draft, sourceText: value };
            if (sectionId === SECTION_NATURAL_LESS_THAN_TARGET) return { ...draft, targetText: value };
            return null;
    }
}